import React, { useState, useEffect } from 'react';

const TypewriterCode = () => {
  const [displayedLines, setDisplayedLines] = useState([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [showMatrixMode, setShowMatrixMode] = useState(false);
  const [matrixLineIndex, setMatrixLineIndex] = useState(0);
  const [matrixCharIndex, setMatrixCharIndex] = useState(0);

  const codeLines = [
    { text: "// Building ChillFree TV with AI", color: "text-teal-400", delay: 200 },
    { text: "const platform = await claude.create({", color: "text-white", delay: 300 },
    { text: '  content: "classic-entertainment",', color: "text-orange-400", delay: 200 },
    { text: '  technology: "cutting-edge-ai",', color: "text-white", delay: 200 },
    { text: '  experience: "netflix-quality"', color: "text-teal-400", delay: 200 },
    { text: "});", color: "text-white", delay: 200 }
  ];

  const matrixLines = [
    { text: "Wake up, Neo!", color: "text-green-400", delay: 2000 },
    { text: "Follow the white rabbit.", color: "text-green-400", delay: 1000 }
  ];

  useEffect(() => {
    // Cursor blinking effect
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);

    return () => clearInterval(cursorInterval);
  }, []);

  // Matrix mode timer - activate after 3 minutes (180000ms)
  useEffect(() => {
    const matrixTimer = setTimeout(() => {
      setShowMatrixMode(true);
      setDisplayedLines([]);
      setMatrixLineIndex(0);
      setMatrixCharIndex(0);
    }, 180000); // 3 minutes

    return () => clearTimeout(matrixTimer);
  }, []);

  // Matrix mode typing effect
  useEffect(() => {
    if (!showMatrixMode || matrixLineIndex >= matrixLines.length) return;

    const currentLine = matrixLines[matrixLineIndex];
    
    if (matrixCharIndex === 0) {
      const initialDelay = matrixLineIndex === 0 ? 500 : 300;
      
      const timer = setTimeout(() => {
        setMatrixCharIndex(1);
      }, initialDelay);
      
      return () => clearTimeout(timer);
    }

    if (matrixCharIndex <= currentLine.text.length) {
      const timer = setTimeout(() => {
        const newDisplayedLines = [...displayedLines];
        
        while (newDisplayedLines.length <= matrixLineIndex) {
          newDisplayedLines.push({ text: '', color: 'text-green-400' });
        }
        
        newDisplayedLines[matrixLineIndex] = {
          ...currentLine,
          text: currentLine.text.substring(0, matrixCharIndex)
        };
        
        setDisplayedLines(newDisplayedLines);
        setMatrixCharIndex(prev => prev + 1);
      }, 80 + Math.random() * 40);

      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        setMatrixLineIndex(prev => prev + 1);
        setMatrixCharIndex(0);
      }, currentLine.delay);

      return () => clearTimeout(timer);
    }
  }, [matrixLineIndex, matrixCharIndex, displayedLines, showMatrixMode]);

  // Normal code typing effect
  useEffect(() => {
    if (showMatrixMode || currentLineIndex >= codeLines.length) return;

    const currentLine = codeLines[currentLineIndex];
    
    if (currentCharIndex === 0) {
      const initialDelay = currentLineIndex === 0 ? 300 : 200;
      
      const timer = setTimeout(() => {
        setCurrentCharIndex(1);
      }, initialDelay);
      
      return () => clearTimeout(timer);
    }

    if (currentCharIndex <= currentLine.text.length) {
      const timer = setTimeout(() => {
        const newDisplayedLines = [...displayedLines];
        
        while (newDisplayedLines.length <= currentLineIndex) {
          newDisplayedLines.push({ text: '', color: 'text-white' });
        }
        
        newDisplayedLines[currentLineIndex] = {
          ...currentLine,
          text: currentLine.text.substring(0, currentCharIndex)
        };
        
        setDisplayedLines(newDisplayedLines);
        setCurrentCharIndex(prev => prev + 1);
      }, 50 + Math.random() * 50);

      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        setCurrentLineIndex(prev => prev + 1);
        setCurrentCharIndex(0);
      }, currentLine.delay);

      return () => clearTimeout(timer);
    }
  }, [currentLineIndex, currentCharIndex, displayedLines, codeLines, showMatrixMode]);

  const currentActiveIndex = showMatrixMode ? matrixLineIndex : currentLineIndex;
  const maxLines = showMatrixMode ? matrixLines.length : codeLines.length;

  return (
    <div className="p-8 bg-gradient-to-br from-black to-gray-900 h-80 font-mono text-sm flex flex-col justify-start overflow-hidden w-full max-w-md sm:max-w-lg md:max-w-xl mx-auto">
      <div className="space-y-3 min-h-full">
        {displayedLines.map((line, index) => (
          <div key={index} className={`${line.color} leading-relaxed whitespace-nowrap overflow-hidden`}>
            {line.text}
            {index === currentActiveIndex && currentActiveIndex < maxLines && showCursor && (
              <span className="animate-pulse text-white">|</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TypewriterCode;

